import { removeWhiteSpace } from '../utils/utils'
import { saveLocalBracket } from '../utils/utils'
import { useAuthContext } from '../contexts/AuthContext'
import { BracketPeriod, getBracketPeriod, useRootContext } from '../contexts/RootContext'
import { useToast } from '../contexts/ToastContext'
import { WC_NAME } from '../utils/Constants'
import AdvancingThirdPlaceForm from './AdvancingThirdPlaceForm'
import BracketFormBracketV2 from './BracketFormBracketV2'
import GroupFormV2 from './GroupFormV2'
import { buildKnockoutStageV2 } from './knockoutStageV2'
import React, { useEffect, useState } from 'react'
import PageLoading from '../misc/PageLoading'
import Unavailable from '../Error/Unavailable'
import { Input, Modal } from 'antd'
import { useNavigate } from 'react-router-dom'

export const GROUP_RANKINGS_KEY = 'bracket-v2-group-rankings'
export const ADVANCING_THIRD_PLACE_KEY = 'bracket-v2-advancing-third-place'
export const KNOCKOUT_PICKS_KEY = 'bracket-v2-knockout-picks'
export const STEP_KEY = 'bracket-v2-step'

const clearSavedBracketProgress = () => {
  localStorage.removeItem(GROUP_RANKINGS_KEY)
  localStorage.removeItem(ADVANCING_THIRD_PLACE_KEY)
  localStorage.removeItem(KNOCKOUT_PICKS_KEY)
  localStorage.removeItem(STEP_KEY)
}

const phaseBackgroundClass = (step) => {
  if (step === 2) return 'bracket-form-background bracket-form-background-phase-two'
  if (step === 3) return 'bracket-form-background bracket-form-background-phase-three'
  return 'bracket-form-background bracket-form-background-phase-one'
}

const initializeGroupRankings = () => {
  try {
    return JSON.parse(localStorage.getItem(GROUP_RANKINGS_KEY)) ?? {}
  } catch (_error) {
    return {}
  }
}

const initializeThirdPlaceTeams = () => {
  try {
    return JSON.parse(localStorage.getItem(ADVANCING_THIRD_PLACE_KEY)) ?? []
  } catch (_error) {
    return []
  }
}

const initializeStep = () => {
  return Number(localStorage.getItem(STEP_KEY)) || 1
}

const initializeKnockoutPicks = () => {
  try {
    return JSON.parse(localStorage.getItem(KNOCKOUT_PICKS_KEY)) ?? {}
  } catch (_error) {
    return {}
  }
}

const KNOCKOUT_PICK_KEYS = [
  ...Array.from({ length: 16 }, (_, idx) => `pick_r2_${idx + 1}`),
  ...Array.from({ length: 8 }, (_, idx) => `pick_q${idx + 1}`),
  ...Array.from({ length: 4 }, (_, idx) => `pick_s${idx + 1}`),
  'pick_f1',
  'pick_f2',
  'pick_winner',
  'pick_third',
]

const extractKnockoutPicks = (picks = {}) => {
  return KNOCKOUT_PICK_KEYS.reduce((knockoutOnlyPicks, key) => {
    if (picks[key] !== undefined && picks[key] !== null && picks[key] !== '') {
      knockoutOnlyPicks[key] = picks[key]
    }
    return knockoutOnlyPicks
  }, {})
}

const readEditingGroupRankings = (editing) => {
  const picks = editing?.picks ?? {}
  return picks._group_rankings ?? null
}

const readEditingAdvancingThirdPlaceTeams = (editing) => {
  const picks = editing?.picks ?? {}
  return picks._advancing_third_place_teams ?? null
}

const sortTeamIdsByRank = (teamIds, rankings) => {
  return [...teamIds].sort((teamA, teamB) => {
    const rankA = typeof rankings[String(teamA)]?.rank === 'number' ? rankings[String(teamA)].rank : Number.MAX_SAFE_INTEGER
    const rankB = typeof rankings[String(teamB)]?.rank === 'number' ? rankings[String(teamB)].rank : Number.MAX_SAFE_INTEGER

    if (rankA !== rankB) return rankA - rankB

    const nameA = rankings[String(teamA)]?.name ?? String(teamA)
    const nameB = rankings[String(teamB)]?.name ?? String(teamB)
    return nameA.localeCompare(nameB)
  })
}

const pickTopSeed = (teamIds, rankings) => sortTeamIdsByRank(teamIds.filter(Boolean), rankings)[0]

const autoFillKnockoutBracket = ({ currentPicks, rankings, roundOnePicks }) => {
  const nextPicks = { ...roundOnePicks, ...currentPicks }
  const roundOf32 = [
    ['pick_1', 'pick_2', 'pick_r2_1'],
    ['pick_3', 'pick_4', 'pick_r2_2'],
    ['pick_5', 'pick_6', 'pick_r2_3'],
    ['pick_7', 'pick_8', 'pick_r2_4'],
    ['pick_9', 'pick_10', 'pick_r2_5'],
    ['pick_11', 'pick_12', 'pick_r2_6'],
    ['pick_13', 'pick_14', 'pick_r2_7'],
    ['pick_15', 'pick_16', 'pick_r2_8'],
    ['pick_17', 'pick_18', 'pick_r2_9'],
    ['pick_19', 'pick_20', 'pick_r2_10'],
    ['pick_21', 'pick_22', 'pick_r2_11'],
    ['pick_23', 'pick_24', 'pick_r2_12'],
    ['pick_25', 'pick_26', 'pick_r2_13'],
    ['pick_27', 'pick_28', 'pick_r2_14'],
    ['pick_29', 'pick_30', 'pick_r2_15'],
    ['pick_31', 'pick_32', 'pick_r2_16'],
  ]
  const roundOf16 = [
    ['pick_r2_2', 'pick_r2_5', 'pick_q1'],
    ['pick_r2_1', 'pick_r2_3', 'pick_q2'],
    ['pick_r2_4', 'pick_r2_6', 'pick_q3'],
    ['pick_r2_7', 'pick_r2_8', 'pick_q4'],
    ['pick_r2_11', 'pick_r2_12', 'pick_q5'],
    ['pick_r2_9', 'pick_r2_10', 'pick_q6'],
    ['pick_r2_14', 'pick_r2_16', 'pick_q7'],
    ['pick_r2_13', 'pick_r2_15', 'pick_q8'],
  ]
  const quarterFinals = [
    ['pick_q1', 'pick_q2', 'pick_s1'],
    ['pick_q5', 'pick_q6', 'pick_s2'],
    ['pick_q3', 'pick_q4', 'pick_s3'],
    ['pick_q7', 'pick_q8', 'pick_s4'],
  ]
  const semiFinals = [
    ['pick_s1', 'pick_s2', 'pick_f1'],
    ['pick_s3', 'pick_s4', 'pick_f2'],
  ]

  roundOf32.forEach(([topField, bottomField, outputField]) => {
    nextPicks[outputField] = pickTopSeed([nextPicks[topField], nextPicks[bottomField]], rankings)
  })

  roundOf16.forEach(([topField, bottomField, outputField]) => {
    nextPicks[outputField] = pickTopSeed([nextPicks[topField], nextPicks[bottomField]], rankings)
  })

  quarterFinals.forEach(([topField, bottomField, outputField]) => {
    nextPicks[outputField] = pickTopSeed([nextPicks[topField], nextPicks[bottomField]], rankings)
  })

  semiFinals.forEach(([topField, bottomField, outputField]) => {
    nextPicks[outputField] = pickTopSeed([nextPicks[topField], nextPicks[bottomField]], rankings)
  })

  nextPicks.pick_winner = pickTopSeed([nextPicks.pick_f1, nextPicks.pick_f2], rankings)

  const thirdPlaceCandidates = [
    nextPicks.pick_s1 && nextPicks.pick_s1 !== nextPicks.pick_f1 ? nextPicks.pick_s1 : null,
    nextPicks.pick_s2 && nextPicks.pick_s2 !== nextPicks.pick_f1 ? nextPicks.pick_s2 : null,
    nextPicks.pick_s3 && nextPicks.pick_s3 !== nextPicks.pick_f2 ? nextPicks.pick_s3 : null,
    nextPicks.pick_s4 && nextPicks.pick_s4 !== nextPicks.pick_f2 ? nextPicks.pick_s4 : null,
  ].filter(Boolean)

  nextPicks.pick_third = pickTopSeed(thirdPlaceCandidates, rankings)

  return nextPicks
}

export default function BracketFormPhaseOne({ authorizationHeaders, createCode, editing, poolId, updateBracket }) {
  const navigate = useNavigate()
  const { isAdmin } = useAuthContext()
  const { secondChanceAvailable, setBracketsLoaded } = useRootContext()
  const secondChance = Boolean(editing?.second_chance || (!editing && secondChanceAvailable && getBracketPeriod() !== BracketPeriod.Open))
  const persistedEditingGroupRankings = readEditingGroupRankings(editing)
  const persistedEditingAdvancingThirdPlaceTeams = readEditingAdvancingThirdPlaceTeams(editing)
  const initialGroupRankings = editing ? (persistedEditingGroupRankings ?? {}) : initializeGroupRankings()
  const initialAdvancingThirdPlaceTeams = editing ? (persistedEditingAdvancingThirdPlaceTeams ?? []) : initializeThirdPlaceTeams()
  const initialKnockoutPicks = editing ? extractKnockoutPicks(editing.picks) : initializeKnockoutPicks()
  const [existingBracketNames, setExistingBracketNames] = useState([])
  const [scorecard, setScorecard] = useState(null)
  const [rankings, setRankings] = useState({})
  const [groupRankings, setGroupRankings] = useState(initialGroupRankings)
  const [advancingThirdPlaceTeams, setAdvancingThirdPlaceTeams] = useState(initialAdvancingThirdPlaceTeams)
  const [knockoutPicks, setKnockoutPicks] = useState(initialKnockoutPicks)
  const [step, setStep] = useState(editing || secondChance ? 3 : initializeStep)
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saveModalVisible, setSaveModalVisible] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [savePending, setSavePending] = useState(false)
  const { errorToast, successToast } = useToast()

  useEffect(() => {
    const params = new URLSearchParams()
    if (poolId) params.set('pool', poolId)
    if (createCode) params.set('create_code', createCode)

    fetch(`/api/v2/scorecard/current${params.toString() ? `?${params.toString()}` : ''}`, {
      credentials: 'same-origin',
      headers: authorizationHeaders,
    })
      .then(response => {
        if (response.ok) {
          return response.json()
        }
        throw response
      })
      .then(body => {
        setScorecard(editing?.scorecard ?? body.scorecard)
        setRankings(body.rankings ?? {})
        setExistingBracketNames(body.brackets ?? [])
        setLoadFailed(!(editing?.scorecard ?? body.scorecard) || (editing && !secondChance && (!persistedEditingGroupRankings || !persistedEditingAdvancingThirdPlaceTeams)))
        setLoading(false)
      })
      .catch(error => {
        const message = error?.statusText || 'Unable to load scorecard'
        errorToast(`${error.status || 500} (${message})`)
        setLoadFailed(true)
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (!editing) localStorage.setItem(GROUP_RANKINGS_KEY, JSON.stringify(groupRankings))
  }, [editing, groupRankings])

  useEffect(() => {
    if (!editing) localStorage.setItem(ADVANCING_THIRD_PLACE_KEY, JSON.stringify(advancingThirdPlaceTeams))
  }, [advancingThirdPlaceTeams, editing])

  useEffect(() => {
    if (!editing) localStorage.setItem(KNOCKOUT_PICKS_KEY, JSON.stringify(knockoutPicks))
  }, [editing, knockoutPicks])

  useEffect(() => {
    const validThirdPlaceTeams = Object.values(groupRankings)
      .map(placements => placements[2])
      .filter(Boolean)
      .map(value => String(value))

    setAdvancingThirdPlaceTeams(current =>
      current.filter(value => validThirdPlaceTeams.includes(String(value)))
    )
  }, [groupRankings])

  useEffect(() => {
    if (!editing) localStorage.setItem(STEP_KEY, String(step))
  }, [editing, step])

  useEffect(() => {
    if (secondChance) {
      setStep(3)
      return
    }

    if (!scorecard) return

    const groupEntries = Object.entries(scorecard.groups ?? {})
    const allGroupsRanked = groupEntries.length > 0 && groupEntries.every(([groupName, teams]) => (groupRankings[groupName] ?? []).length === teams.length)

    if (!allGroupsRanked && step > 1) {
      setStep(1)
    } else if (allGroupsRanked && advancingThirdPlaceTeams.length !== 8 && step > 2) {
      setStep(2)
    }
  }, [advancingThirdPlaceTeams.length, groupRankings, scorecard, secondChance, step])

  const buildRoundOneContext = () => {
    if (secondChance) {
      const roundOnePicks = Array.from({ length: 32 }, (_, idx) => idx + 1).reduce((picks, pickNumber) => {
        picks[`pick_${pickNumber}`] = scorecard?.picks?.[`pick_${pickNumber}`]
        return picks
      }, {})

      return {
        roundOnePicks,
        roundOneSourceLabels: {},
      }
    }

    const { roundOnePicks, roundOneSourceLabels } = buildKnockoutStageV2(groupRankings, advancingThirdPlaceTeams)
    return { roundOnePicks, roundOneSourceLabels }
  }

  const handleKnockoutChange = (value, key) => {
    setKnockoutPicks(current => ({ ...current, [key]: value }))
  }

  const handleAutoFillKnockout = () => {
    const { roundOnePicks } = buildRoundOneContext()

    setKnockoutPicks(current => autoFillKnockoutBracket({
      currentPicks: current,
      rankings,
      roundOnePicks,
    }))
  }

  const handleClearKnockout = () => {
    setKnockoutPicks({})
  }

  const validateKnockoutBracket = () => {
    const { roundOnePicks } = buildRoundOneContext()
    const picks = { ...roundOnePicks, ...knockoutPicks }
    const requiredKeys = [
      ...Array.from({ length: 32 }, (_, idx) => `pick_${idx + 1}`),
      ...Array.from({ length: 16 }, (_, idx) => `pick_r2_${idx + 1}`),
      ...Array.from({ length: 8 }, (_, idx) => `pick_q${idx + 1}`),
      ...Array.from({ length: 4 }, (_, idx) => `pick_s${idx + 1}`),
      'pick_f1',
      'pick_f2',
      'pick_winner',
      'pick_third',
    ]

    return requiredKeys.every(key => picks[key] !== undefined && picks[key] !== null && picks[key] !== '')
  }

  const buildPersistedPicks = () => {
    const { roundOnePicks } = buildRoundOneContext()

    if (secondChance) {
      return {
        ...roundOnePicks,
        ...knockoutPicks,
      }
    }

    return {
      ...roundOnePicks,
      ...knockoutPicks,
      _group_rankings: groupRankings,
      _advancing_third_place_teams: advancingThirdPlaceTeams,
    }
  }

  const submitSaveBracket = () => {
    if (savePending) return

    const trimmedName = saveName.trim()
    if (!trimmedName) {
      errorToast('Bracket name cannot be blank.')
      return
    }

    if (existingBracketNames.some(existingName => existingName.toLowerCase() === trimmedName.toLowerCase())) {
      errorToast('A bracket with this name already exists.')
      return
    }

    const picks = buildPersistedPicks()
    setSavePending(true)

    fetch('/api/v2/brackets', {
      method: 'POST',
      body: JSON.stringify({
        create_code: createCode,
        name: trimmedName,
        picks,
        pool: poolId ?? null,
      }),
      credentials: 'same-origin',
      headers: authorizationHeaders,
    })
      .then(async response => {
        if (response.ok) return response.json()
        const errorBody = await response.json().catch(() => ({}))
        throw { status: response.status, message: errorBody.error || response.statusText }
      })
      .then(body => {
        saveLocalBracket(body.id)
        setBracketsLoaded(false)
        clearSavedBracketProgress()
        successToast('Bracket saved.')
        navigate('/leaderboard')
      })
      .catch(error => {
        errorToast(`${error.status || 500} (${error.message || 'Unable to save bracket'})`)
      })
      .finally(() => {
        setSavePending(false)
      })
  }

  const handleSaveBracket = () => {
    if (savePending) return

    if (!validateKnockoutBracket()) {
      errorToast('Complete the bracket before saving.')
      return
    }

    if (editing) {
      updateBracket(buildPersistedPicks())
      return
    }

    setSaveModalVisible(true)
  }

  if (loading) {
    return <div className={removeWhiteSpace(WC_NAME)}>
      <div className={`background-image ${phaseBackgroundClass(1)}`}/>
      <PageLoading />
    </div>
  }

  if (loadFailed || !scorecard) {
    return <div className={removeWhiteSpace(WC_NAME)}>
      <div className={`background-image ${phaseBackgroundClass(1)}`}/>
      <Unavailable
        title="Phase one is not ready yet"
        subTitle="The current v2 scorecard could not be loaded."
      />
    </div>
  }

  return (
    <div className={removeWhiteSpace(WC_NAME)}>
      <div className={`background-image ${phaseBackgroundClass(step)}`}/>
      {step === 1 && (
        <GroupFormV2
          groupRankings={groupRankings}
          isAdmin={isAdmin}
          rankings={rankings}
          scorecard={scorecard}
          setGroupRankings={setGroupRankings}
          setStep={setStep}
        />
      )}
      {step === 2 && (
        <AdvancingThirdPlaceForm
          advancingThirdPlaceTeams={advancingThirdPlaceTeams}
          groupRankings={groupRankings}
          isAdmin={isAdmin}
          rankings={rankings}
          setAdvancingThirdPlaceTeams={setAdvancingThirdPlaceTeams}
          setStep={setStep}
        />
      )}
      {step === 3 && (
        <BracketFormBracketV2
          advancingThirdPlaceTeams={advancingThirdPlaceTeams}
          clearKnockout={handleClearKnockout}
          groupRankings={groupRankings}
          autoFillKnockout={handleAutoFillKnockout}
          handleChange={handleKnockoutChange}
          isAdmin={isAdmin}
          knockoutPicks={knockoutPicks}
          rankings={rankings}
          saveLabel={editing ? 'Save changes' : 'Save bracket'}
          saveBracket={handleSaveBracket}
          savePending={savePending}
          showBackButton={!secondChance}
          roundOneContext={buildRoundOneContext()}
          scorecard={scorecard}
          setStep={setStep}
        />
      )}
      <Modal
        title="Save bracket"
        visible={saveModalVisible}
        destroyOnClose
        onCancel={() => {
          if (savePending) return
          setSaveModalVisible(false)
        }}
        onOk={submitSaveBracket}
        okText="Save"
        confirmLoading={savePending}
      >
        <Input
          autoFocus
          placeholder="Bracket name"
          value={saveName}
          onChange={(event) => setSaveName(event?.target?.value ?? '')}
          onPressEnter={submitSaveBracket}
        />
      </Modal>
    </div>
  )
}
